import { useAuth } from "@/shared/hooks/useAuth";
import { Button } from "@/shared/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/shared/ui/avatar";
import { LogOut, ChevronUp } from "lucide-react";
import { useSidebar } from "@/shared/ui/sidebar";
import { useState } from "react";

export function SidebarFooter() {
  const { profile, user, signOut } = useAuth();
  const { state } = useSidebar();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const getInitials = () => {
    if (profile?.first_name && profile?.last_name) {
      return `${profile.first_name.charAt(0)}${profile.last_name.charAt(0)}`.toUpperCase();
    }
    if (user?.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return "U";
  };

  const getDisplayName = () => {
    const firebaseName = (user?.user_metadata?.full_name as string | undefined)?.trim();
    if (firebaseName) {
      return firebaseName;
    }
    if (profile?.first_name && profile?.last_name) {
      return `${profile.first_name} ${profile.last_name}`;
    }
    return getUserType();
  };

  const getUserType = () => {
    if (!profile?.user_type) return "User";
    
    const typeMap: Record<string, string> = {
      'property_owner': 'Property Owner',
      'home_association': 'Home Association',
      'facilities_management': 'Facilities Management',
      'service_provider': 'Service Provider',
      'admin': 'Administrator'
    };
    
    return typeMap[profile.user_type] || 'User';
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Mini sidebar mode - show only avatar
  if (state === "collapsed") {
    return (
      <div 
        className="p-2" 
        onClick={(e) => e.stopPropagation()} 
        onMouseEnter={(e) => e.stopPropagation()}
        onMouseLeave={(e) => e.stopPropagation()}
        data-dropdown-open={isDropdownOpen}
      >
        <DropdownMenu onOpenChange={setIsDropdownOpen}>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              className="w-full h-12 p-0 hover:bg-sidebar-accent text-sidebar-foreground focus-visible:ring-0 focus-visible:ring-offset-0"
              onClick={(e) => e.stopPropagation()}
            >
              <Avatar className="h-8 w-8 border-2 border-primary/20">
                <AvatarFallback className="bg-primary text-primary-foreground font-semibold text-sm">
                  {getInitials()}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent 
            align="end" 
            className="w-56 mb-2"
            side="right"
          >
            <div className="px-2 py-1.5 text-sm">
              <p className="font-medium text-foreground">{getDisplayName()}</p>
              <p className="text-xs text-muted-foreground">{getUserType()}</p>
            </div>
            {/* MVP2-282: Commented out separator, profile link, and settings link */}
            {/* <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/owner/profile" className="flex items-center text-foreground">
                <User className="mr-2 h-4 w-4" />
                Profile
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/owner/settings/notifications" className="flex items-center text-foreground">
                <Settings className="mr-2 h-4 w-4" />
                Settings
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator /> */}
            <DropdownMenuItem 
              onClick={handleSignOut}
              className="text-destructive focus:text-destructive cursor-pointer"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  }

  // Full sidebar mode - show complete user info
  return (
    <div 
      className="border-t border-sidebar-border bg-gradient-to-r from-primary/5 to-primary/10"
      onClick={(e) => e.stopPropagation()}
      onMouseEnter={(e) => e.stopPropagation()}
      onMouseLeave={(e) => e.stopPropagation()}
      data-dropdown-open={isDropdownOpen}
    >
      <DropdownMenu onOpenChange={setIsDropdownOpen}>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="ghost" 
            className="w-full justify-between h-auto p-4 hover:bg-sidebar-accent/50 text-sidebar-foreground focus-visible:ring-0 focus-visible:ring-offset-0"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center space-x-3 min-w-0 flex-1">
              <Avatar className="h-10 w-10 border-2 border-primary/20 flex-shrink-0">
                <AvatarFallback className="bg-primary text-primary-foreground font-semibold">
                  {getInitials()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 text-left min-w-0">
                <p className="text-sm font-semibold text-sidebar-foreground truncate">{getDisplayName()}</p>
                {profile?.first_name && profile?.last_name && (
                  <p className="text-xs text-sidebar-foreground/70 truncate">
                    {getUserType()}
                  </p>
                )}
                {user?.email && (
                  <p className="text-[10px] text-sidebar-foreground/60 truncate leading-tight">
                    {user.email}
                  </p>
                )}
              </div>
            </div>
            <ChevronUp className="h-4 w-4 text-sidebar-foreground/70 flex-shrink-0" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent 
          align="end" 
          className="w-56 mb-2"
          side="top"
        >
          {/* MVP2-282: Commented out profile link, settings link, and separator */}
          {/* <DropdownMenuItem asChild>
            <Link to="/owner/profile" className="flex items-center text-foreground">
              <User className="mr-2 h-4 w-4" />
              Profile
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/owner/settings/notifications" className="flex items-center text-foreground">
              <Settings className="mr-2 h-4 w-4" />
              Settings
            </Link>
          </DropdownMenuItem>
          <DropdownMenuSeparator /> */}
          <DropdownMenuItem 
            onClick={handleSignOut}
            className="text-destructive focus:text-destructive cursor-pointer"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Sign Out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
